'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import './direction.css';
export default function WorkspaceLogin({
  partner = false,
  support = false,
  returnTo,
}: {
  partner?: boolean;
  support?: boolean;
  returnTo?: string;
}) {
  const [email, setEmail] = useState(''),
    [password, setPassword] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [signup, setSignup] = useState(false),
    [visible, setVisible] = useState(false);
  const target = support
    ? returnTo?.startsWith('/direction/support/connecter?')
      ? returnTo
      : '/direction/support'
    : partner
      ? '/partenaires'
      : '/direction';
  const check = async () => {
    const r = await fetch(
      support
        ? '/api/support?access=1'
        : partner
          ? '/api/partenaires/access'
          : '/api/direction?view=access',
      {
        cache: 'no-store',
      }
    );
    if (!r.ok) {
      const d = await r.json();
      throw Error(d.error);
    }
    location.assign(target);
  };
  useEffect(() => {
    createClient()
      .auth.getUser()
      .then(({ data }) => {
        if (data.user) check().catch((e) => setError(e.message));
      })
      .catch(() => {});
  }, []);
  return (
    <div className="dir-login">
      <div className="dir-login-story">
        <Link href="/" className="dir-logo">
          <img src="/assets/universes/logo.png" alt="" width="54" height="54" />
          <span>
            <b>
              RDV<span>PARTNERS</span>
            </b>
            <small>Votre ambition. Notre engagement.</small>
          </span>
        </Link>
        <div>
          <p className="dir-eyebrow">
            {support
              ? 'VOTRE ESPACE SUPPORT'
              : partner
                ? 'VOTRE ESPACE PARTENAIRE'
                : 'VOTRE ESPACE DIRECTION'}
          </p>
          <h1>
            {support
              ? 'À l’écoute de vos clients.'
              : partner
                ? 'De belles rencontres.'
                : 'Une vision claire.'}
            <em>
              {support
                ? 'Des réponses qui avancent.'
                : partner
                  ? 'Des projets à construire.'
                  : 'Les bonnes décisions.'}
            </em>
          </h1>
          <p>
            {support
              ? 'Retrouvez les demandes clients, les réponses proposées et les corrections à valider.'
              : partner
                ? 'Construisez les demandes de vos entreprises avec Élina et retrouvez chaque projet transmis.'
                : 'Retrouvez vos demandes, vos devis et votre activité dans un même espace de travail.'}
          </p>
        </div>
        <span>RDV-Partners · L’intelligence à vos côtés.</span>
      </div>
      <main className="dir-login-form">
        <p className="dir-eyebrow">
          {support
            ? 'SUPPORT RDV-PARTNERS'
            : partner
              ? 'APPORTEURS D’AFFAIRES'
              : 'DIRECTION RDV-PARTNERS'}
        </p>
        <h2>{signup ? 'Créer mon compte' : 'Heureux de vous retrouver.'}</h2>
        <p>
          {support
            ? 'Accès réservé à contact@rdvpartners.com et maryna.gourpil@rdvpartners.com. Confirmez votre adresse pour activer votre accès.'
            : partner
              ? 'Utilisez l’adresse email enregistrée par RDV-Partners sur votre fiche partenaire.'
              : 'Connectez-vous avec votre compte professionnel.'}
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError('');
            setNotice('');
            try {
              const sb = createClient();
              if (signup) {
                const r = await sb.auth.signUp({ email: email.trim(), password });
                if (r.error) throw r.error;
                setNotice(
                  'Consultez votre messagerie pour confirmer votre adresse, puis connectez-vous ici.'
                );
                setSignup(false);
              } else {
                const r = await sb.auth.signInWithPassword({ email: email.trim(), password });
                if (r.error)
                  throw Error('Adresse email ou mot de passe incorrect, ou compte non confirmé.');
                await check();
              }
            } catch (e) {
              setError(e instanceof Error ? e.message : 'La connexion a échoué.');
            } finally {
              setBusy(false);
            }
          }}
        >
          <label className="dir-field">
            <span>Adresse email</span>
            <input
              type="email"
              autoComplete="email"
              required
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="dir-field">
            <span>Mot de passe</span>
            <div className="dir-password">
              <input
                type={visible ? 'text' : 'password'}
                autoComplete={signup ? 'new-password' : 'current-password'}
                required
                minLength={signup ? 12 : 1}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                onClick={() => setVisible(!visible)}
              >
                {visible ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </div>
          </label>
          {error && (
            <p className="dir-alert error" role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p className="dir-alert" role="status">
              {notice}
            </p>
          )}
          <button className="dir-btn primary" disabled={busy}>
            {busy ? 'Vérification…' : signup ? 'Créer mon compte' : 'Ouvrir mon espace'}
            <ArrowRight size={18} />
          </button>
        </form>
        <Link href="/auth/forgot-password">Mot de passe oublié ?</Link>
        {support && (
          <button
            className="dir-text-button"
            type="button"
            onClick={async () => {
              await createClient().auth.signOut({ scope: 'local' });
              setError('');
              setNotice('Vous pouvez maintenant vous connecter avec votre compte Support.');
            }}
          >
            Changer de compte
          </button>
        )}
        {(partner || support) && (
          <button
            className="dir-text-button"
            onClick={() => {
              setSignup(!signup);
              setError('');
            }}
          >
            {signup ? 'J’ai déjà un compte' : 'Première connexion ? Créer mon compte'}
          </button>
        )}
        <Link href="/" className="dir-login-back">
          ← Revenir au site
        </Link>
      </main>
    </div>
  );
}
